import type dayjs from 'dayjs'

export interface EventSearchQuery {
  search?: string
  startDate?: string
  endDate?: string
}

export interface EventSearchResult {
  _id: string
  eventId?: string
  title: string
  description: string
  startTime: string | Date | dayjs.Dayjs
  endTime: string | Date | dayjs.Dayjs
  groupId: string
  locationId: string
  createdBy: string
  visibility: {
    type: EventVisibilityType
    level?: string
    course?: string
    privateChannel?: string
  }
  eventPricing: {
    price: number
    currency: string
    isTestMode: boolean
    paymentProductId?: string
    paymentPriceId?: string
    _id?: string
  }
  eventColor: string
  eventLocation: {
    location: string
    locationUrl: string
    hideEventLocationFromNonAttendees: boolean
    _id?: string
  }
  timezone: string
  eventCoverUrl: string
  remindOneDayBefore: boolean
  status: string
  originalRecurringEventId: string | null
  isFree: boolean
  isPartOfPlan?: boolean
  isRecurring: boolean
  isCancelled: boolean
  hideAttendees: boolean
  recurringMeta?: {
    repeatInterval?: string
    repeatOccurence?: number
    repeatsOn?: string[]
    recurringEndDate?: string | Date | dayjs.Dayjs
    monthlyRepeatType?: string
    afterOccurrences?: number
    isNeverEnds?: string
    rrule: string
    excludeDates: string[]
    _id?: string
  }
  notification?: {
    notifyAt: string
    cloudTaskId: string
    _id: string
  }
  createdAt: string
  updatedAt: string
  __v: number
  nextOccurrence?: string
  rsvpMeta?: {
    hasRegistered: boolean
    paymentMeta?: {
      paymentStatus: string
      orderId: string
    }
    _id: string
  }
}

/** Membership snapshot passed into `openEvent` from in-tree calendar/list openers. */
export interface OpenEventMembership {
  isActiveMember: boolean
  onJoinRequired: () => void
}

export interface EventMentionData {
  id: string
  label: string
  type: 'events-mention'
  timestamp: number
  startTime: string
  endTime: string
  nextOccurrence?: string
  isRecurring?: boolean
  eventCoverUrl?: string
  description?: string
}

export enum SidebarEventsListSectionKey {
  UPCOMING = 'upcoming',
  PAST = 'past',
}

export enum CalendarViewTypes {
  WEEK = 'week',
  MONTH = 'month',
}

export interface SidebarEventsListSection {
  title: string
  key: SidebarEventsListSectionKey
}

export const DEFAULT_EVENT_COVER_URL = 'https://storage.googleapis.com/revex-communities-staging/assets/events_image_placeholder.webp'

// New colors, from new app.
export enum EventColor {
  ORANGE = '#EA580C',
  RED = '#DB2777',
  BLUE = '#0000FF',
  GREEN = '#16A34A',
  PURPLE = '#9333EA',
  INDIGO = '#4F46E5',
  YELLOW = '#0891B2',
}

export enum EventVisibilityType {
  ALL = 'PUBLIC',
  LEVEL = 'LEVEL',
  COURSE = 'COURSE',
  CHANNEL = 'CHANNEL',
}

export enum RecurringEndType {
  ON = 'on',
  AFTER = 'after',
  NEVER = 'never',
}

export enum MeetingType {
  ONE_TIME = 'one_time',
  RECURRING = 'recurring',
}

export interface MeetingCreateResponse {
  _id: string
  title: string
  status: string
  sourceEntityId: string
  meetingUrl: string
}

// FullCalendar callback types
export interface CalendarMoreLinkArg {
  text: string
}

export interface CalendarDayHeaderArg {
  text: string
}

export interface CalendarEventClickInfo {
  event?: {
    extendedProps?: EventSearchResult
  }
}

export interface CalendarFetchInfo {
  start: Date | string
  end: Date | string
}

export interface CalendarEventContentInfo {
  view?: {
    type?: string
  }
  event: {
    start?: Date | null
    end?: Date | null
    startStr: string
    title: string
    extendedProps?: Record<string, unknown>
  }
  timeText: string
}

export interface CalendarDatesSetInfo {
  view: {
    currentStart: Date
    title?: string
  }
}

export interface CalendarEventClassNamesArg {
  event?: {
    rsvpMeta?: {
      hasRegistered?: boolean
    }
  }
}

export interface CalendarDateClickInfo {
  date?: Date
}

export interface EventFormDataType {
  _id?: string
  selectedStartTime?: Date | string | dayjs.Dayjs | null
  selectedEndTime?: Date | string | dayjs.Dayjs | null

  title: string
  description: string
  eventCoverUrl?: string
  eventColor: string
  timezone?: string
  hideAttendees: boolean
  remindOneDayBefore: boolean
  startTime: Date | string | dayjs.Dayjs
  endTime: Date | string | dayjs.Dayjs
  isFree: boolean
  isCancelled: boolean
  isRecurring: boolean
  timeZone?: string
  recurringEndType: 'on' | 'after' | 'never'
  updateCurrentOccurrence?: boolean
  updateCurrentAndAllFutureOccurrences?: boolean
  updateAllOccurrences?: boolean
  recurringMeta: {
    eventStart?: Date | string | dayjs.Dayjs
    eventEnd?: Date | string | dayjs.Dayjs
    rrule?: string
    excludeDates?: Array<Date | string | dayjs.Dayjs>
    repeatOccurence?: number
    repeatInterval?: string
    repeatsOn?: string[]
    recurringEndDate?: Date | string | dayjs.Dayjs | null
    monthlyRepeatType?: string
    afterOccurrences?: number
    isNeverEnds?: boolean
  }
  notification: {
    notifyAt?: string
    cloudTaskId?: string
  }
  status: 'active' | 'cancelled'
  eventLocation: {
    location?: string
    locationUrl?: string
    hideEventLocationFromNonAttendees: boolean
  }
  visibility: {
    type: EventVisibilityType
    level?: string
    course?: string
    privateChannel?: string
  }
  eventPricing: {
    price?: number
    currency?: string
    isTestMode: boolean
  }
}

export enum RepeatIntervalOptions {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export enum RecurrenceType {
  NONE = 'none',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY_DATE = 'monthly_day',
  MONTHLY_WEEKDAY = 'monthly_weekday',
  YEARLY = 'yearly',
  CUSTOM = 'custom',
}

export interface MonthlyRepeatOption {
  labelKey: string
  labelParams: Record<string, string>
  value: string
}

export interface CalendarEventOverrides {
  start?: string | null
  end?: string | null
}

export interface CalendarEvent extends Omit<EventSearchResult, 'groupId'> {
  backgroundColor: string
  borderColor: string
  textColor: string
  groupIdEvent: string
  groupId: undefined
  extendedProps: Omit<EventSearchResult, 'groupId'> & CalendarEventOverrides & { groupId: undefined }
}
